import { Fragment, forwardRef, memo } from 'react';
import map from 'lodash/map';
import { useIntl } from 'react-intl';
// Material UI imports
import Box from '@mui/material/Box';
import MenuList from '@mui/material/MenuList';
// Skillmore UI Components
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
// local imports
import { CategoriesGroup } from '../models/categoriesGroup';
import CategoryMenuItem from './CategoryMenuItem';
// SCSS imports
import { panel, staticPanel, menu } from './CategoriesMenu.module.scss';

type CategoriesMenuProps = {
  readonly groups?: CategoriesGroup[];
  readonly value?: number;
  readonly onSelect: (value: number) => void;
  readonly required?: boolean;
  readonly withoutScroll?: boolean;
};

const CategoriesMenu = forwardRef<HTMLDivElement, CategoriesMenuProps>(({
  groups,
  value,
  onSelect,
  required = false,
  withoutScroll = false
}, ref) => {
  const { formatMessage } = useIntl();
  return (
    <Box ref={ref} className={withoutScroll ? staticPanel : panel}>
      {map(groups, ({ header, categories, icon }) => {
        const headerStr = formatMessage({ id: header });
        const content = (
          <>
            <BoxTypography px={2} pt={2} pb={0.5} variant="body2" fontStyle="italic" color="info.caption">
              {headerStr}
            </BoxTypography>
            <MenuList
                tabIndex={0}
                aria-label={headerStr}
                className={menu}
            >
              {required ? undefined : (
                <CategoryMenuItem
                    id={0}
                    selected={value === 0}
                    onClick={onSelect}
                />
              )}
              {map(categories, ({ id, title, name, values }) => (
                <CategoryMenuItem
                    key={id}
                    id={id}
                    title={name || title}
                    values={values}
                    icon={icon}
                    selected={id === value}
                    onClick={onSelect}
                />
              ))}
            </MenuList>
          </>
        );
        return withoutScroll ? (
          <Box key={header}>
            {content}
          </Box>
        ) : (
          <Fragment key={header}>
            {content}
          </Fragment>
        );
      })}
    </Box>
  );
});

CategoriesMenu.displayName = 'CategoriesMenu';

export default memo(CategoriesMenu);
